/// <amd-module name="Wit/DeliveryPlans/Common/Utils/DateUtils" />

export interface ISpan {
    startDate: Date;
    endDate: Date;
}

/** Compares dates, but ignores the time part */
export function compareDate(a: Date, b: Date) {
    if (a.getFullYear() !== b.getFullYear()) {
        return a.getFullYear() - b.getFullYear();
    }
    if (a.getMonth() !== b.getMonth()) {
        return a.getMonth() - b.getMonth();
    }
    return a.getDate() - b.getDate();
}

/** Returns true if b is the next day following a */
export function isNextDay(a: Date, b: Date) {
    return compareDate(incrementDate(a), b) === 0;
}

/** Returns the day following the given one */
export function incrementDate(d: Date) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
}

/** returns <0 if a is before b, 0 if a and b intersect, and >0 if a is after b */
export function compareSpans(a: ISpan, b: ISpan) {
    if (compareDate(a.endDate, b.startDate) < 0) {
        return -1;
    }
    if (compareDate(a.startDate, b.endDate) > 0) {
        return 1;
    }
    return 0;
}

/** return the number of days in the month */
export function daysInMonth(d: Date): number {
    // get the 0th day of the next month, which is the last day of the month
    return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

/**
 * return the number of days between two dates
 */
export function daysInSpan(span: ISpan): number;
export function daysInSpan(start: Date, end: Date): number;
export function daysInSpan(start: ISpan | Date, end?: Date): number {
    if (!(start instanceof Date)) {
        end = start.endDate;
        start = start.startDate;
    }
    if (!end) {
        // should be possible, but TypeScript worries
        throw Error("end date required");
    }
    // strip out the time
    const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const diff = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.round(diff / (1000 * 3600 * 24));
}

export function isDateInSpan(date: Date, span: ISpan) {
    return date.getTime() - span.startDate.getTime() > 0 && date.getTime() - span.endDate.getTime() < 0;
}

export function addDaysToDate(date: Date, days: number): Date {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
}

/**
 * Get a span spanning the given number of days from the start date.
 * @param startDate
 * @param days
 */
export function getSpan(startDate: Date, days: number): ISpan {
    const endDate = addDaysToDate(startDate, days);

    return {
        startDate,
        endDate
    };
}

export function getSpanFromMidpoint(center: Date, days: number): ISpan {
    const startDate = addDaysToDate(center, days / -2);
    const endDate = addDaysToDate(center, days / 2);

    return {
        startDate,
        endDate
    };
}

export function shiftSpan(span: ISpan, daysToShift: number): ISpan {
    return { startDate: addDaysToDate(span.startDate, daysToShift), endDate: addDaysToDate(span.endDate, daysToShift) };
}

export function today() {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
}

/**
 * Return a Date object with the UTC time of the supplied Date
 * @param date
 */
export function getDateInUTC(date: Date): Date {
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * Return a Date object with the local time of the supplied Date
 * Assumes the passed date is in UTC
 * @param date
 */
export function getDateInLocal(date: Date): Date {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
}
